import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  useHasUnpublishedRequestRestrictions_slot$data,
  useHasUnpublishedRequestRestrictions_slot$key,
} from "./__generated__/useHasUnpublishedRequestRestrictions_slot.graphql";

type ConfiguredRequestRestrictionEdge =
  | {
      readonly liftAt: string | null | undefined;
      readonly requestCloses: string | null | undefined;
    }
  | null
  | undefined;

export const hasUnpublishedRequestRestrictions = (
  configuredRequestRestrictionEdges:
    | readonly ConfiguredRequestRestrictionEdge[]
    | null
    | undefined
) => {
  return (
    configuredRequestRestrictionEdges?.some((edge) => edge?.liftAt == null) ??
    false
  );
};

export const hasClosedUnpublishedRequestRestrictions = (
  configuredRequestRestrictionEdges:
    | readonly ConfiguredRequestRestrictionEdge[]
    | null
    | undefined,
  nowMs: number = Date.now()
) => {
  return (
    configuredRequestRestrictionEdges?.some((edge) => {
      if (edge?.liftAt != null) {
        return false;
      }

      const requestClosesMs = edge?.requestCloses
        ? Date.parse(edge.requestCloses)
        : null;

      return (
        requestClosesMs !== null &&
        !Number.isNaN(requestClosesMs) &&
        requestClosesMs < nowMs
      );
    }) ?? false
  );
};

const getConfiguredRequestRestrictionEdges = (
  slot: useHasUnpublishedRequestRestrictions_slot$data | null | undefined
) => {
  if (slot?.__typename === "QueueSlot") {
    return slot.agenda?.configuredRequestRestrictions?.edges;
  }

  if (slot?.__typename === "TimeSlot") {
    return slot.configuredRequestRestrictions?.edges;
  }

  return null;
};

export const useHasUnpublishedRequestRestrictions = (
  slotRef: useHasUnpublishedRequestRestrictions_slot$key | null
) => {
  const slot = useFragment(
    useHasUnpublishedRequestRestrictionsFragmentString,
    slotRef
  );

  return hasUnpublishedRequestRestrictions(
    getConfiguredRequestRestrictionEdges(slot)
  );
};

export const useHasClosedUnpublishedRequestRestrictions = (
  slotRef: useHasUnpublishedRequestRestrictions_slot$key | null
) => {
  const slot = useFragment(
    useHasUnpublishedRequestRestrictionsFragmentString,
    slotRef
  );

  return hasClosedUnpublishedRequestRestrictions(
    getConfiguredRequestRestrictionEdges(slot)
  );
};

const useHasUnpublishedRequestRestrictionsFragmentString = graphql`
  fragment useHasUnpublishedRequestRestrictions_slot on Slot {
    __typename
    ... on QueueSlot {
      agenda {
        configuredRequestRestrictions: requestWindowBookingRules(
          type: [REQUEST]
        ) {
          edges {
            liftAt
            requestCloses
          }
        }
      }
    }
    ... on TimeSlot {
      configuredRequestRestrictions: requestWindowBookingRules(
        type: [REQUEST]
      ) {
        edges {
          liftAt
          requestCloses
        }
      }
    }
  }
`;
